/**
 * Cliente HTTP centralizado con manejo automático de autenticación
 * - Añade el token JWT automáticamente a las peticiones
 * - Detecta respuestas 401 y dispara logout
 * - Verifica expiración del token antes de hacer peticiones
 */

import { isTokenExpired } from './jwt.utils';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface RequestOptions {
  headers?: Record<string, string>;
  body?: any;
  skipAuth?: boolean;
}

// Callback para manejar logout cuando el token expira o es inválido
let onUnauthorizedCallback: (() => void) | null = null;

/**
 * Registra el callback que se ejecutará cuando ocurra un error 401
 * Esto permite que el AuthProvider maneje el logout
 */
export function setUnauthorizedCallback(callback: () => void) {
  onUnauthorizedCallback = callback;
}

/**
 * Obtiene el token del localStorage
 */
function getToken(): string | null {
  return localStorage.getItem('token');
}

/**
 * Verifica si el token actual es válido (existe y no está expirado)
 */
export function hasValidToken(): boolean {
  const token = getToken();
  if (!token) return false;
  return !isTokenExpired(token);
}

/**
 * Cliente HTTP principal
 */
async function request<T = any>(
  url: string,
  method: HttpMethod,
  options: RequestOptions = {}
): Promise<T> {
  const { headers = {}, body, skipAuth = false } = options;
  
  // Verificar token antes de hacer la petición (solo para rutas autenticadas)
  if (!skipAuth) {
    const token = getToken();
    
    if (!token) {
      onUnauthorizedCallback?.();
      throw new Error('No authentication token found');
    }
    
    if (isTokenExpired(token)) {
      onUnauthorizedCallback?.();
      throw new Error('Authentication token has expired');
    }
    
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Configurar headers por defecto
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };
  
  const config: RequestInit = {
    method,
    headers: defaultHeaders,
  };
  
  if (body && method !== 'GET') {
    config.body = typeof body === 'string' ? body : JSON.stringify(body);
  }
  
  try {
    const response = await fetch(url, config);
    
    // Manejar respuestas 401 Unauthorized
    if (response.status === 401) {
      onUnauthorizedCallback?.();
      throw new Error('Unauthorized - Session expired');
    }
    
    // Manejar errores HTTP
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP Error: ${response.status}`);
    }
    
    // Intentar parsear como JSON, si falla retornar texto
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return text as T;
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Métodos HTTP convenientes
 */
export const apiClient = {
  get: <T = any>(url: string, options?: RequestOptions) => 
    request<T>(url, 'GET', options),
    
  post: <T = any>(url: string, body?: any, options?: RequestOptions) => 
    request<T>(url, 'POST', { ...options, body }),
    
  put: <T = any>(url: string, body?: any, options?: RequestOptions) => 
    request<T>(url, 'PUT', { ...options, body }),
    
  patch: <T = any>(url: string, body?: any, options?: RequestOptions) => 
    request<T>(url, 'PATCH', { ...options, body }),
    
  delete: <T = any>(url: string, options?: RequestOptions) => 
    request<T>(url, 'DELETE', options),
};

export default apiClient;

